import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useExperience } from '@/store/experience';

const vertex = /* glsl */ `
attribute vec3 iPos; attribute float iScale; attribute float iRot; attribute float iVar; attribute float iPhase;
uniform float uTime; uniform vec3 uRayOrigin; uniform vec3 uRayDir; uniform float uPush;
varying vec2 vUv; varying vec2 vTile; varying float vDist; varying float vShade;
void main(){
  vUv = uv;
  float v = clamp(floor(iVar + 0.5), 0.0, 3.0);
  vTile = vec2(mod(v, 2.0), floor(v / 2.0)) * 0.5;
  vec3 p = iPos;
  p.x += sin(uTime * 0.06 + iPhase) * 1.6;
  p.y += sin(uTime * 0.09 + iPhase * 1.7) * 0.5;
  // Pointer interaction: clouds puff away from the cursor ray, then drift back
  vec3 toP = p - uRayOrigin;
  float along = max(dot(toP, uRayDir), 0.1);
  vec3 perp = toP - uRayDir * along;
  float d = length(perp) / (along * 0.22 + 1.0);
  float push = (1.0 - smoothstep(0.0, 2.6, d)) * uPush;
  vec3 pushDir = length(perp) > 0.001 ? normalize(perp) : vec3(0.0, 1.0, 0.0);
  p += pushDir * push * (0.6 + iScale * 0.06);
  float s = iScale * (1.0 + push * 0.18);
  float c = cos(iRot + push * 0.15), sn = sin(iRot + push * 0.15);
  vec2 q = vec2(position.x * c - position.y * sn, position.x * sn + position.y * c) * s;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  mv.xy += q;
  vDist = -mv.z;
  vShade = position.y;
  gl_Position = projectionMatrix * mv;
}`;

const fragment = /* glsl */ `
uniform sampler2D uMap; uniform vec3 uSunColor; uniform vec3 uShadowColor; uniform vec3 uFogColor;
uniform float uOpacity; uniform float uFogNear; uniform float uFogFar; uniform float uNearFade; uniform float uWhite;
varying vec2 vUv; varying vec2 vTile; varying float vDist; varying float vShade;
void main(){
  vec2 luv = clamp(vUv, 0.002, 0.998);
  float a = texture2D(uMap, luv * 0.5 + vTile).a;
  // Soft box window: a quad edge can never be visible whatever the texture returns
  vec2 w = smoothstep(vec2(0.0), vec2(0.12), luv) * smoothstep(vec2(0.0), vec2(0.12), 1.0 - luv);
  a *= w.x * w.y;
  float light = smoothstep(-0.55, 0.5, vShade);
  vec3 col = mix(uShadowColor, uSunColor, light);
  float fog = smoothstep(uFogNear, uFogFar, vDist);
  col = mix(col, uFogColor, fog * 0.85);
  col = mix(col, vec3(1.0), uWhite * 0.6);
  float nearFade = smoothstep(0.4, uNearFade, vDist);
  float alpha = a * uOpacity * nearFade * (1.0 - fog * 0.75);
  if (alpha < 0.003) discard;
  #ifdef CLOUD_DEBUG
  gl_FragColor = vec4(1.0, 0.0, 0.0, max(alpha, 0.3));
  #else
  gl_FragColor = vec4(col, alpha);
  #endif
}`;

export function CloudField({ clouds, texture, layer }) {
  const meshRef = useRef();
  const { camera } = useThree();
  const frame = useExperience((s) => s.frame);
  const count = clouds.length;

  const { geometry, order, dist } = useMemo(() => {
    const geo = new THREE.InstancedBufferGeometry().copy(new THREE.PlaneGeometry(1, 1));
    geo.instanceCount = count;
    const pos = new Float32Array(count * 3), scale = new Float32Array(count), rot = new Float32Array(count), vr = new Float32Array(count), ph = new Float32Array(count);
    geo.setAttribute('iPos', new THREE.InstancedBufferAttribute(pos, 3));
    geo.setAttribute('iScale', new THREE.InstancedBufferAttribute(scale, 1));
    geo.setAttribute('iRot', new THREE.InstancedBufferAttribute(rot, 1));
    geo.setAttribute('iVar', new THREE.InstancedBufferAttribute(vr, 1));
    geo.setAttribute('iPhase', new THREE.InstancedBufferAttribute(ph, 1));
    return { geometry: geo, order: Array.from({ length: count }, (_, i) => i), dist: new Float32Array(count) };
  }, [count]);

  const uniforms = useMemo(() => ({
    uMap: { value: texture }, uTime: { value: 0 },
    uRayOrigin: { value: new THREE.Vector3() }, uRayDir: { value: new THREE.Vector3(0, 0, -1) }, uPush: { value: 0 },
    uSunColor: { value: new THREE.Color('#ffffff') }, uShadowColor: { value: new THREE.Color(layer === 'front' ? '#b9d3f2' : '#9dbde6') },
    uFogColor: { value: new THREE.Color('#8fc1ec') },
    uOpacity: { value: layer === 'front' ? 0.75 : 0.95 }, uFogNear: { value: 40 }, uFogFar: { value: 150 },
    uNearFade: { value: layer === 'front' ? 2.2 : 6 }, uWhite: { value: 0 },
  }), [texture, layer]);

  const ray = useMemo(() => new THREE.Raycaster(), []);
  const pushTarget = useRef(0);

  useFrame((state, dt) => {
    const mat = meshRef.current?.material;
    if (!mat) return;
    const u = mat.uniforms;
    u.uTime.value = state.clock.elapsedTime;
    u.uWhite.value = frame.fog;
    // Depth sort so soft alpha blends correctly (cheap for a few hundred instances)
    const cp = camera.position;
    for (let i = 0; i < count; i++) { const c = clouds[i]; dist[i] = (c.x - cp.x) ** 2 + (c.y - cp.y) ** 2 + (c.z - cp.z) ** 2; }
    order.sort((a, b) => dist[b] - dist[a]);
    const g = geometry.attributes;
    for (let k = 0; k < count; k++) {
      const c = clouds[order[k]];
      g.iPos.array[k * 3] = c.x; g.iPos.array[k * 3 + 1] = c.y; g.iPos.array[k * 3 + 2] = c.z;
      g.iScale.array[k] = c.s; g.iRot.array[k] = c.rot; g.iVar.array[k] = c.v; g.iPhase.array[k] = c.ph;
    }
    g.iPos.needsUpdate = g.iScale.needsUpdate = g.iRot.needsUpdate = g.iVar.needsUpdate = g.iPhase.needsUpdate = true;

    ray.setFromCamera(frame.pointer, camera);
    u.uRayOrigin.value.copy(ray.ray.origin);
    u.uRayDir.value.copy(ray.ray.direction);
    pushTarget.current += (frame.pointerStrength - pushTarget.current) * Math.min(1, dt * 3);
    u.uPush.value = pushTarget.current;
  });

  return (
    <mesh ref={meshRef} geometry={geometry} frustumCulled={false} renderOrder={layer === 'front' ? 3 : 1}>
      <shaderMaterial vertexShader={vertex} fragmentShader={fragment} uniforms={uniforms} transparent depthWrite={false} depthTest={false} defines={window.location.search.includes('clouddebug') ? { CLOUD_DEBUG: 1 } : {}} />
    </mesh>
  );
}
